import Backbone from 'backbone'
import template from './dataset-template-form.hbs'
import _ from 'lodash'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import FormView from 'scoreboard/components/form-view'
import SimpleDatasetFieldSelector from 'scoreboard/datasets/simple-dataset-field-selector'
import extraInputs from './dataset-template-form-extra-inputs.hbs'
import Dataset from 'scoreboard/datasets/model/dataset'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import selects from 'scoreboard/components/selects'
import * as allowRecordlessCopies from './allow-recordless-copies'
const showAllowRecordlessCopiesOption = allowRecordlessCopies.showAllowRecordlessCopiesOption
const allowRecordlessCopiesLabel = allowRecordlessCopies.allowRecordlessCopiesLabel
import highlight from 'scoreboard/components/highlight'
import HttpStatusCodes from 'http-status-codes'
import ConfirmPrompt from 'scoreboard/components/confirm-prompt'
import StrongConfirmPrompt from 'scoreboard/components/strong-confirm-prompt'
import appDisplay from 'scoreboard/components/app-display'
import unwrapModel from 'scoreboard/components/unwrap-model'
import permissions from 'scoreboard/components/permissions'
import formatValue from 'scoreboard/components/format-value'
import OrganizationTemplatePermissionsPanel from './organization-template-permissions-panel'
import OrganizationTemplatePermissions from './organization-template-permissions'

import GuardConstants from 'scoreboard/components/unsaved/guard-constants'
const { HIDE_WARNING } = GuardConstants

const DatasetTemplateForm = Backbone.View.extend({
	tagName: 'form',
	className: 'dataset-template-form',

	/**
	 * @param {Object} opt
	 *
	 * @param {Dataset} [opt.dataset] if a dataset was already chosen (ie user
	 * completed this screen and then hit Back on the wizard), the dataset that
	 * was selected
	 *
	 * @param {OrganizationTemplatePermissions} [opt.organizationTemplatePermissions]
	 * any permissions that were already defined (ie user completed this screen and
	 * hit Back on the wizard)
	 */
	initialize: function(opt = {}) {
		this.options = opt

		this.organizationTemplatePermissions =
			this.options.organizationTemplatePermissions ?? new OrganizationTemplatePermissions()
	},

	render: function() {
		this.$el.empty().append(template({
			heading: this.options.wizard.dialogTitle(),
			instructions: i18n.get('template.paste.datasetSelectionInstructions',
				highlight(this.model.get('label')))
		}))

		let TemplateFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new TemplateFormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: true,
			// #55854 If user is in the wizard, the regular save guard is confusing,
			// so this just gives them "Don't Save" and "Back to Form" buttons
			// (leaving out a "Save" button)
			guardOptions: {
				messages: {
					prompt: i18n.get('forms.guard.prompt'),
					save: i18n.get('forms.guard.save')
				},
				hideCancel: true,
				keepChangesFn: () => HIDE_WARNING
			}
		})

		// will be null when renaming an org that is already part of a template
		if (this.options.wizard) {
			// clear out any existing buttons and replace them with wizard buttons
			this.$('.dialog-footer').empty()

			this.options.wizard.render({
				formView: this.formView,
				doneKey: 'common.next'
			})
		}

		let data = { datasets: { } }
		if (this.options.dataset) {
			data.datasets[this.options.dataset.id] = unwrapModel(this.options.dataset)
		}

		let renderFieldDropdownOptions = ({ dataset }) => {
			// #26368 Only use the dataset's native fields (so don't use combinedFields)
			let allowedFieldIds = dataset.get('fields')
				.filter(field => {
					// #44808 don't allow date/time fields
					return !isDateTimeDataType(field.dataType)
				})
				.map(field => field.id)

			return { allowedFieldIds }
		}

		this.fieldSelector = new SimpleDatasetFieldSelector({
			data,
			initial: {
				domainObjectId: this.options.dataset ? this.options.dataset.id : null,
				datasetAggregationFieldId: this.options.datasetFieldId,
				datasetFilters: this.options.templateFilters
			},
			// #52836 relative date filters don't make any sense in the context
			// of an org template
			allowAppCalendar: false,
			showAggregationType: false,
			showDateField: false,
			allowTotal: false,
			// #26368 no VLFs
			allowLinkedFields: false,
			renderFieldDropdownOptions
		})

		this.$('.dialog-body-content').append(this.fieldSelector.el)
		this.fieldSelector.render()

		this.listenTo(this.fieldSelector, 'change', () => {
			this.formView.clearErrors()
			this.formView.change()

			let info = this.selectedDatasetInfo()
			this.updateUseRangeSection(info?.underlyingField)
			this.updateCreateRecordlessOrgsSection(info?.underlyingField)
			this.updatePermissionsPanel({
				dataset: info?.dataset,
				field: info?.underlyingField
			})
		})

		this.fieldSelector.on('noPermission', () => {
			this.$el.addClass('no-permission')
			this.$('.button-primary').addClass('is-disabled')
		})

		this.$('.fields-container .panel-content').append(extraInputs())

		let underlyingField
		if (this.options.dataset && this.options.datasetFieldId) {
			let field = this.options.dataset.combinedFieldsById[this.options.datasetFieldId]
			underlyingField = this.options.dataset.getUnderlyingField(field)
		}

		this.listenTo(this.fieldSelector, 'load', () => {
			this.updateUseRangeSection(underlyingField, this.options.useDatasetRanges)
			this.updateCreateRecordlessOrgsSection(underlyingField, this.options.allowRecordlessCopies)
			this.updatePermissionsPanel({
				dataset: this.options.dataset,
				field: underlyingField
			})
			this.$('[name=useRanges]').on('change', () => {
				this.updateCreateRecordlessOrgsSection(
					this.selectedDatasetInfo()?.underlyingField)
			})
			this.formView.start()
		})

		let initialPermissionsState
		if (this.options.dataset && this.options.datasetFieldId) {
			initialPermissionsState = {
				dataset: this.options.dataset,
				field: this.options.dataset.getUnderlyingField(this.options.datasetFieldId),
				rollupTreeGroups: this.options.rollupTreeGroups
			}
		}

		this.permissionsPanel = new OrganizationTemplatePermissionsPanel({
			collection: this.organizationTemplatePermissions,
			organizationTemplateId: this.model.get('id'),
			initial: initialPermissionsState
		})
		this.$('.fields-container').after(this.permissionsPanel.el)
		this.permissionsPanel.render()

		return this
	},

	updateUseRangeSection: function(field, val) {
		let show = this._showUseRangeSection(field)

		this.$('.use-range-section').toggleClass('is-not-displayed', !show)

		let select = this.$('.use-range-section select')

		if (show) {
			var fieldLabel = field.label
			var categoryOrRangeSuffix = isNumericDataType(field.dataType) ?
				'numeric' : 'text'
			var categoryOrRange = 'datasets.choose.rows.range.' + categoryOrRangeSuffix

			select.find('option[value=false]').html(i18n.get('datasets.choose.rows.values', fieldLabel))
			select.find('option[value=true]').html(i18n.get(categoryOrRange, fieldLabel))
		}

		if (!_.isNil(val)) {
			select.val(val.toString())
		}

		selects(select)
	},

	_showUseRangeSection: function(field) {
		if (field) {
			// only show the select box if the field has ranges/categories
			return !_.isEmpty(field.ranges)
		} else {
			return false
		}
	},

	updateCreateRecordlessOrgsSection: function(field, val) {
		let show = this._showCreateRecordlessOrgsSection(field)
		let section = this.$('.create-recordless-organizations-section')
		section.toggleClass('is-not-displayed', !show)

		if (show) {
			section.find('.label-text').text(allowRecordlessCopiesLabel(field))
		}

		if (!_.isNil(val)) {
			section.find('input').prop('checked', val)
		}
	},

	_showCreateRecordlessOrgsSection: function(field) {
		return showAllowRecordlessCopiesOption(field, this.useRanges())
	},

	updatePermissionsPanel ({ dataset, field }) {
		this.permissionsPanel?.datasetAndField({ dataset, field })
	},

	test: function() {
		// fieldSelector.selected() returns null if not everything is complete.
		return this.fieldSelector && !!this.fieldSelector.selected()
			? null : i18n.get('common.error.mustSelect.datasetAndField')
	},

	submit: function() {
		let selected = this.fieldSelector.selected()

		let datasetSettings = {
			datasetFieldId: selected.datasetAggregationFieldId,
			templateFilters: selected.datasetFilters,
			useDatasetRanges: this.useRanges(),
			allowRecordlessCopies: this.createRecordlessOrgs()
		}

		let wizardOptions = _.extend({},
			this.options, datasetSettings, this.permissionsPanel?.gatherData(),
			{ dataset: new Dataset(selected.domainObject) })

		$.post({
			url: `api/organizations/${this.options.orgId}/validate-dataset-template`,
			data: JSON.stringify(datasetSettings),
			success: (data) => {
				const {expectedCopyCount} = data
				const formattedCount = formatValue({
					value: expectedCopyCount,
					format: 'number',
					shorten: false
				})
				let next = () => this.options.wizard.next(wizardOptions)

				let confirmed = () => {
					// Normally we'd do confirmation.trigger('done'), but we then get
					// into a race condition with wizard.next() and the logic to close
					// dialogs on transitionEnd. Instead, popDialog() will ensure that
					// the confirmation dialog is closed and cleaned properly, and then
					// we can safely transition to wizard.next()
					appDisplay.popDialog()
					next()
				}

				let cancelConfirm = () => {
					// #32337 if user clicked No in the warning, clear out the
					// "submitting" state by calling formView.start()
					this.formView.start()
				}

				if (expectedCopyCount > 100 && !permissions.appAdmin()) {
					const html = i18n.get(
						'template.paste.datasetSelection.exceededOrgCount',
						highlight(formattedCount))
					this.formView.errorHtml(html)
				} else if (expectedCopyCount > 1000) {
					// If more than 1000 orgs will be created, make the user give a
					// "strong" confirmation (typing "confirm" into a text box)
					let confirmation = new StrongConfirmPrompt({
						dialogTitle: i18n.get('template.paste.confirmation.dialogTitle'),
						message: i18n.get('template.paste.datasetSelection.expectedOrgCount', formattedCount),
						prompt: i18n.get('template.paste.confirmation.prompt'),
						confirmationText: i18n.get('common.confirm'),
						confirmationButton: i18n.get('common.next'),
						showUndoneWarning: false
					})

					this.listenTo(confirmation, 'confirm', confirmed)
					this.listenTo(confirmation, 'cancel', cancelConfirm)

					appDisplay.dialog(confirmation)
				} else if (expectedCopyCount > 50) {
					// #19120 if more than 50 but less than 1000 orgs will be created,
					// just give them a simple "are you sure" confirmation
					let ContinueConfirm = ConfirmPrompt.extend({
						// Overriding submit() so we can use appDisplay.popDialog(),
						// see explanation in confirmed()
						submit(e) {
							e && e.preventDefault()
							confirmed()
						}

					})

					let confirmation = new ContinueConfirm({
						titleKey: 'template.paste.confirmation.dialogTitle',
						messageKey: i18n.get('template.paste.datasetSelection.expectedOrgCount', formattedCount)
					})

					this.listenTo(confirmation, 'cancel', cancelConfirm)

					appDisplay.dialog(confirmation)
				} else {
					next()
				}
			},
			error: response => {
				if (response.status == HttpStatusCodes.BAD_REQUEST &&
					response.responseJSON.messageCode == 'template.paste.datasetFieldAlreadyFilter') {
					let html = i18n.get('template.paste.datasetFieldAlreadyFilter',
						highlight(response.responseJSON.arguments[0]))
					this.formView.errorHtml(html)
				} else {
					this.formView.error(response)
				}
			}
		})
	},

	/**
	 *
	 *  @returns {Object|null} Information about the selected dataset and field
	 *  @property {Dataset} dataset - The selected Dataset
	 *  @property {Object} field - The actual field selected. This is a plain
	 *  Object, not the DatasetField Model
	 *  @property {Object} underlyingField - The underlying field, if the
	 *  selected field is linked. This is a plain Object, not the DatasetField Model
	 */
	selectedDatasetInfo: function() {
		let selected = this.fieldSelector.selected()
		if (selected) {
			let dataset = new Dataset(selected.domainObject)
			let field = dataset.combinedFieldsById[selected.datasetAggregationFieldId]
			let underlyingField = dataset.getUnderlyingField(field)
			return { dataset, field, underlyingField }
		} else {
			return null
		}
	},

	useRanges: function() {
		return this._showUseRangeSection(this.selectedDatasetInfo()?.underlyingField)
			? this.$('.use-range-section select').val() === 'true'
			: false
	},

	createRecordlessOrgs: function() {
		return this._showCreateRecordlessOrgsSection(this.selectedDatasetInfo()?.underlyingField)
			? this.$('[name=allowRecordlessCopies]').prop('checked')
			: false
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([ this.fieldSelector, this.formView,
				this.permissionsPanel ],'remove')
	}
})

export default DatasetTemplateForm
DatasetTemplateForm.__filename__ = 'organizations/dataset-template-form.js'
